import React, { useEffect, useState, useRef } from 'react';
import "../styles/popupstyles/prodimgpopup.css";
import { getProductGallery } from './galleryAssets';

/**
 * ProdImgPopup Component - Premium Product Gallery Viewer
 * 
 * ✅ PRODUCTION FEATURES:
 * - Dynamically loads images/videos from assets/prodgallery/{category}/{productKey}/
 * - Auto-detects all files in the product folder (no manual imports needed)
 * - Supports: .png, .jpg, .jpeg, .webp, .mp4, .webm
 * - Mouse scroll zoom in/out (1x to 3x) - DESKTOP ONLY
 * - Mobile pinch-to-zoom with drag/pan - MOBILE ONLY (≤768px)
 * - Click + drag to pan when zoomed
 * - Swipe/drag left/right to navigate media when not zoomed
 * - Fullscreen mode for images and videos
 * - Video timeline controls with seek bar
 * - ESC key to reset zoom / close
 * - Navigation arrows
 * - Bottom dots indicator
 * - Product name at TOP CENTER (always visible)
 * - Blur background overlay
 * - Full touch support
 * 
 * Props:
 * @param {string} productName - Name of the product (display only)
 * @param {string} category - Category key (e.g., 'agriculture', 'hospitility')
 * @param {string} productKey - Product folder name (e.g., 'truuna', 'serving')
 * @param {function} onClose - Callback to close the popup
 */
function ProdImgPopup({ productName, category, productKey, onClose }) {
  // ✅ DYNAMIC GALLERY LOADING - Automatically loads all images/videos
  const galleryAssets = getProductGallery(category, productKey);
  
  // Fallback if no assets found
  const hasGallery = galleryAssets && galleryAssets.length > 0;
  
  const [currentMediaIndex, setCurrentMediaIndex] = useState(0);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [dragDistance, setDragDistance] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  
  // Mobile pinch-to-zoom states
  const [isPinching, setIsPinching] = useState(false);
  const [initialPinchDistance, setInitialPinchDistance] = useState(0);
  const [initialPinchZoom, setInitialPinchZoom] = useState(1);
  
  const mediaContainerRef = useRef(null);
  const videoRef = useRef(null);
  const fullscreenContainerRef = useRef(null);

  // Get current media item
  const currentMedia = hasGallery ? galleryAssets[currentMediaIndex] : null;
  const isVideo = currentMedia?.type === 'video';

  // Detect if mobile (≤768px)
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Prevent body scroll when popup is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  // Auto-play videos when they become active
  useEffect(() => {
    if (isVideo && videoRef.current) {
      videoRef.current.play().catch(err => {
        console.log('Video autoplay prevented:', err);
      });
    }
  }, [currentMediaIndex, isVideo]);

  // Pause video when slide changes
  useEffect(() => {
    return () => {
      if (videoRef.current) {
        videoRef.current.pause();
      }
    };
  }, [currentMediaIndex]);

  // Exit fullscreen on popup close
  useEffect(() => {
    return () => {
      if (document.fullscreenElement) {
        document.exitFullscreen().catch(err => console.log('Exit fullscreen error:', err));
      }
    };
  }, []);

  // Handle overlay click to close
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Keyboard navigation and zoom reset
  useEffect(() => {
    const handleKeyDown = (e) => {
      switch(e.key) {
        case 'Escape':
          if (isFullscreen) {
            exitFullscreen();
          } else if (zoomLevel > 1) {
            resetZoom();
          } else {
            onClose();
          }
          break;
        case 'ArrowLeft':
          if (zoomLevel === 1) {
            navigatePrev();
          }
          break;
        case 'ArrowRight':
          if (zoomLevel === 1) {
            navigateNext();
          }
          break;
        default:
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [zoomLevel, currentMediaIndex, isFullscreen]);

  // Mouse wheel zoom (DESKTOP ONLY - only for images)
  const handleWheel = (e) => {
    if (isVideo || isMobile) return; // Disable zoom for videos and mobile
    
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.2 : 0.2;
    const newZoom = Math.min(Math.max(1, zoomLevel + delta), 3);
    
    setZoomLevel(newZoom);
    
    if (newZoom === 1) {
      setPosition({ x: 0, y: 0 });
    }
  };

  // Calculate distance between two touch points
  const getTouchDistance = (touch1, touch2) => {
    const dx = touch1.clientX - touch2.clientX;
    const dy = touch1.clientY - touch2.clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  // Reset zoom and position
  const resetZoom = () => {
    setZoomLevel(1);
    setPosition({ x: 0, y: 0 });
  };

  // Navigation functions
  const navigateNext = () => {
    if (!hasGallery) return;
    setCurrentMediaIndex((prev) => (prev + 1) % galleryAssets.length);
    resetZoom();
  };

  const navigatePrev = () => {
    if (!hasGallery) return;
    setCurrentMediaIndex((prev) => (prev - 1 + galleryAssets.length) % galleryAssets.length);
    resetZoom();
  };

  const goToMedia = (index) => {
    setCurrentMediaIndex(index);
    resetZoom();
  };

  // Fullscreen functionality
  const enterFullscreen = () => {
    const element = fullscreenContainerRef.current;
    if (!element) return;

    const requestFullscreen = element.requestFullscreen || 
                              element.webkitRequestFullscreen || 
                              element.mozRequestFullScreen || 
                              element.msRequestFullscreen;

    if (requestFullscreen) {
      requestFullscreen.call(element).then(() => {
        setIsFullscreen(true);
      }).catch(err => {
        console.log('Fullscreen request failed:', err);
      });
    }
  };

  const exitFullscreen = () => {
    const exitFS = document.exitFullscreen || 
                   document.webkitExitFullscreen || 
                   document.mozCancelFullScreen || 
                   document.msExitFullscreen;

    if (exitFS && document.fullscreenElement) {
      exitFS.call(document).then(() => {
        setIsFullscreen(false);
      }).catch(err => {
        console.log('Exit fullscreen failed:', err);
      });
    }
  };

  const toggleFullscreen = () => {
    if (isFullscreen) {
      exitFullscreen();
    } else {
      enterFullscreen();
    }
  };

  // Listen for fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('msfullscreenchange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('msfullscreenchange', handleFullscreenChange);
    };
  }, []);

  // Mouse/Touch drag functionality
  const handleMouseDown = (e) => {
    e.preventDefault();
    setIsDragging(true);
    setDragStart({
      x: e.clientX - position.x,
      y: e.clientY - position.y
    });
    setDragDistance(0);
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    
    const newX = e.clientX - dragStart.x;
    const newY = e.clientY - dragStart.y;
    
    const distance = Math.abs(newX - position.x) + Math.abs(newY - position.y);
    setDragDistance(distance);
    
    if (zoomLevel > 1 && !isVideo) {
      // Zoomed image: Pan
      const maxOffset = 200 * (zoomLevel - 1);
      setPosition({
        x: Math.max(-maxOffset, Math.min(maxOffset, newX)),
        y: Math.max(-maxOffset, Math.min(maxOffset, newY))
      });
    } else {
      // Not zoomed or video: Horizontal drag for navigation
      const horizontalDrag = newX - position.x;
      if (Math.abs(horizontalDrag) > 80) {
        if (horizontalDrag > 0) {
          navigatePrev();
        } else {
          navigateNext();
        }
        setIsDragging(false);
        setPosition({ x: 0, y: 0 });
      } else {
        setPosition({ x: horizontalDrag, y: 0 });
      }
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    if (zoomLevel === 1 || isVideo) {
      setPosition({ x: 0, y: 0 });
    }
  };

  // Touch support with pinch-to-zoom (MOBILE ONLY)
  const handleTouchStart = (e) => {
    if (e.touches.length === 2 && isMobile && !isVideo) {
      // Pinch-to-zoom start
      setIsPinching(true);
      const distance = getTouchDistance(e.touches[0], e.touches[1]);
      setInitialPinchDistance(distance);
      setInitialPinchZoom(zoomLevel);
      e.preventDefault();
    } else if (e.touches.length === 1) {
      // Single touch drag
      const touch = e.touches[0];
      setIsDragging(true);
      setDragStart({
        x: touch.clientX - position.x,
        y: touch.clientY - position.y
      });
      setDragDistance(0);
    }
  };

  const handleTouchMove = (e) => {
    if (isPinching && e.touches.length === 2 && isMobile && !isVideo) {
      // Pinch-to-zoom
      e.preventDefault();
      const currentDistance = getTouchDistance(e.touches[0], e.touches[1]);
      const scale = currentDistance / initialPinchDistance;
      const newZoom = Math.min(Math.max(1, initialPinchZoom * scale), 3);
      setZoomLevel(newZoom);
      
      if (newZoom === 1) {
        setPosition({ x: 0, y: 0 });
      }
    } else if (isDragging && e.touches.length === 1) {
      // Single touch drag
      const touch = e.touches[0];
      const newX = touch.clientX - dragStart.x;
      const newY = touch.clientY - dragStart.y;
      
      const distance = Math.abs(newX - position.x) + Math.abs(newY - position.y);
      setDragDistance(distance);
      
      if (zoomLevel > 1 && !isVideo) {
        const maxOffset = 200 * (zoomLevel - 1);
        setPosition({
          x: Math.max(-maxOffset, Math.min(maxOffset, newX)),
          y: Math.max(-maxOffset, Math.min(maxOffset, newY))
        });
      } else {
        const horizontalDrag = newX - position.x;
        if (Math.abs(horizontalDrag) > 80) {
          if (horizontalDrag > 0) {
            navigatePrev();
          } else {
            navigateNext();
          }
          setIsDragging(false);
          setPosition({ x: 0, y: 0 });
        } else {
          setPosition({ x: horizontalDrag, y: 0 });
        }
      }
    }
  };

  const handleTouchEnd = () => {
    setIsPinching(false);
    setIsDragging(false);
    if (zoomLevel === 1 || isVideo) {
      setPosition({ x: 0, y: 0 });
    }
  };

  // Fallback UI if no gallery assets found
  if (!hasGallery) {
    return (
      <div className="prodimg-popup-overlay" onClick={handleOverlayClick}>
        <div className="prodimg-popup-container" onClick={(e) => e.stopPropagation()}>
          <div className="product-name-header">{productName}</div>
          <button className="prodimg-popup-close" onClick={onClose} aria-label="Close gallery">×</button>
          <div className="prodimg-popup-image-wrapper" style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            minHeight: '400px',
            color: 'var(--color-text-secondary)',
            fontSize: '1.2rem'
          }}>
            <div style={{ textAlign: 'center' }}>
              <p>📂 No gallery assets found</p>
              <p style={{ fontSize: '0.9rem', marginTop: '10px' }}>
                Add images/videos to:<br/>
                <code style={{ background: '#f0f0f0', padding: '4px 8px', borderRadius: '4px' }}>
                  assets/prodgallery/{category}/{productKey}/
                </code>
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="prodimg-popup-overlay" onClick={handleOverlayClick}>
      <div 
        ref={fullscreenContainerRef}
        className={`prodimg-popup-container ${isFullscreen ? 'fullscreen-mode' : ''}`} 
        onClick={(e) => e.stopPropagation()}
      >
        {/* Product Name - TOP CENTER */}
        <div className="product-name-header">
          {productName}
        </div>

        {/* Close Button */}
        <button 
          className="prodimg-popup-close"
          onClick={onClose}
          aria-label="Close gallery"
        >
          ×
        </button>

        {/* Navigation Arrows */}
        <button
          className="nav-arrow nav-arrow-left"
          onClick={navigatePrev}
          aria-label="Previous media"
          disabled={zoomLevel > 1}
        >
          ‹
        </button>
        <button
          className="nav-arrow nav-arrow-right"
          onClick={navigateNext}
          aria-label="Next media"
          disabled={zoomLevel > 1}
        >
          ›
        </button>

        {/* Media Container */}
        <div 
          ref={mediaContainerRef}
          className={`prodimg-popup-image-wrapper ${zoomLevel > 1 ? 'zoomed' : ''} ${isDragging ? 'dragging' : ''}`}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onWheel={handleWheel}
          style={{
            cursor: zoomLevel > 1 && !isVideo
              ? (isDragging ? 'grabbing' : 'grab') 
              : (isDragging ? 'grabbing' : 'grab')
          }}
        >
          {isVideo ? (
            <video 
              ref={videoRef}
              src={currentMedia.url}
              className="prodimg-popup-image"
              controls
              controlsList="nodownload"
              loop
              style={{
                maxWidth: '100%',
                maxHeight: '100%',
                objectFit: 'contain',
                borderRadius: 'var(--radius-md)',
                filter: 'drop-shadow(0 4px 12px rgba(0, 0, 0, 0.1))'
              }}
            >
              Your browser does not support video playback.
            </video>
          ) : (
            <img 
              src={currentMedia.url}
              alt={`${productName} - View ${currentMediaIndex + 1}`}
              className="prodimg-popup-image"
              style={{
                transform: `scale(${zoomLevel}) translate(${position.x / zoomLevel}px, ${position.y / zoomLevel}px)`,
                transition: isDragging || isPinching ? 'none' : 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
              }}
              draggable={false}
            />
          )}

          {/* Fullscreen Button */}
          <button
            className="fullscreen-button"
            onClick={toggleFullscreen}
            aria-label={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
            title={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
          >
            {isFullscreen ? (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3" />
              </svg>
            ) : (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3" />
              </svg>
            )}
          </button>
        </div>

        {/* Bottom Controls */}
        <div className="prodimg-popup-bottom">
          {/* Dots Indicator */}
          <div className="dots-indicator">
            {galleryAssets.map((_, index) => (
              <button
                key={index}
                className={`dot ${index === currentMediaIndex ? 'active' : ''}`}
                onClick={() => goToMedia(index)}
                aria-label={`Go to view ${index + 1}`}
                disabled={zoomLevel > 1}
              />
            ))}
          </div>

          {/* Interaction Info */}
          <div className="interaction-info">
            {isVideo ? (
              <>🎥 Video {currentMediaIndex + 1} / {galleryAssets.length} • Drag to navigate</>
            ) : (
              <>
                {zoomLevel > 1 ? (
                  <>🔍 {Math.round(zoomLevel * 100)}% • Drag to pan • {isMobile ? 'Pinch to zoom' : 'Scroll to zoom'}</>
                ) : (
                  <>🖱️ {isMobile ? 'Pinch to zoom' : 'Scroll to zoom'} • Drag to navigate • {currentMediaIndex + 1} / {galleryAssets.length}</>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProdImgPopup;